import React, { useEffect, useState } from 'react';
import {
  useRecordContext,
  useGetList,
  useNotify,
  useDataProvider,
  Loading,
  useRefresh,
} from 'react-admin';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Alert,
  Button,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';

/**
 * Permissions Tab Component
 * 
 * Displays and manages user-specific permissions for admin users.
 * Allows adding/removing direct permissions that override role permissions.
 * 
 * @param {boolean} readOnly - If true, displays permissions in read-only mode without action buttons
 */
const PermissionsTab = ({ readOnly = false }) => {
  const record = useRecordContext();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();

  const [userPermissions, setUserPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Get all available permissions
  const { data: allPermissions, isLoading: permissionsLoading } = useGetList(
    'permissions',
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'name', order: 'ASC' },
    }
  );

  // Fetch user-specific permissions
  useEffect(() => {
    if (!record?.id) return;

    const fetchUserPermissions = async () => {
      try {
        setLoading(true);
        const response = await dataProvider.getList('users', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'id', order: 'ASC' },
          filter: { id: record.id },
          meta: { subResource: 'permissions' },
        });

        const permissions = response.data || [];
        setUserPermissions(permissions);
        
        // Initialize selected permissions
        const permissionIds = new Set(permissions.map(p => p.id));
        setSelectedPermissions(permissionIds);
        setHasChanges(false);
      } catch (error) {
        console.error('Failed to fetch user permissions:', error);
        notify('Failed to load user permissions', { type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchUserPermissions();
  }, [record?.id, dataProvider, notify]);

  const handlePermissionToggle = (permissionId) => {
    const newSelected = new Set(selectedPermissions);
    
    if (newSelected.has(permissionId)) {
      newSelected.delete(permissionId);
    } else {
      newSelected.add(permissionId);
    }
    
    setSelectedPermissions(newSelected);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!record?.id) return;

    try {
      setSaving(true);
      
      // Convert Set to Array
      const permissionIds = Array.from(selectedPermissions);

      await dataProvider.update('users', {
        id: record.id,
        data: { permission_ids: permissionIds },
        previousData: record,
        meta: { subResource: 'permissions' },
      });

      setHasChanges(false);
      notify('User permissions updated successfully', { type: 'success' });
      
      // Refresh to ensure UI is in sync
      refresh();
    } catch (error) {
      console.error('Failed to update permissions:', error);
      notify(error?.message || 'Failed to update user permissions', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const permissionIds = new Set(userPermissions.map(p => p.id));
    setSelectedPermissions(permissionIds);
    setHasChanges(false);
  };

  if (!record) {
    return null;
  }

  // Only show for admin users
  if (!record.isAdmin) {
    return (
      <Box p={2}>
        <Alert severity="info">
          User-specific permissions are only available for admin users. 
          This user's permissions are determined by their role.
        </Alert>
      </Box>
    );
  }

  if (loading || permissionsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Loading />
      </Box>
    );
  }

  // Group permissions by category (prefix before colon)
  const permissionsByCategory = allPermissions?.reduce((acc, permission) => {
    const [category] = permission.name.split(':');
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(permission);
    return acc;
  }, {}) || {};

  const categories = Object.keys(permissionsByCategory).sort();

  return (
    <Box p={2}>
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          User-Specific Permissions
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          These permissions are assigned directly to this user and will override their role permissions.
          The user's role ({record.roleName || 'N/A'}) also provides additional permissions.
        </Typography>
        
        {hasChanges && !readOnly && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You have unsaved changes. Click "Save Permissions" to apply them.
          </Alert>
        )}

        {!readOnly && (
          <Box display="flex" gap={2} mb={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={!hasChanges || saving}
            >
              {saving ? 'Saving...' : 'Save Permissions'}
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
              disabled={!hasChanges || saving}
            >
              Reset
            </Button>

            <Chip 
              label={`${selectedPermissions.size} permissions selected`} 
              color="primary" 
              variant="outlined"
            />
          </Box>
        )}

        {readOnly && (
          <Typography variant="body2" color="textSecondary" paragraph>
            Total Permissions: {selectedPermissions.size}
          </Typography>
        )}
      </Box>

      {categories.map((category) => (
        <Paper key={category} elevation={1} sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ textTransform: 'capitalize' }}>
            {category.replace(/-/g, ' ')}
          </Typography>
          
          <FormGroup>
            {permissionsByCategory[category].map((permission) => (
              <FormControlLabel
                key={permission.id}
                control={
                  <Checkbox
                    checked={selectedPermissions.has(permission.id)}
                    onChange={() => handlePermissionToggle(permission.id)}
                    disabled={saving || readOnly}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" component="span">
                      {permission.name}
                    </Typography>
                    {permission.description && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {permission.description}
                      </Typography>
                    )}
                  </Box>
                }
              />
            ))}
          </FormGroup>
        </Paper>
      ))}
    </Box>
  );
};

export default PermissionsTab;

